// apps/web/src/features/cargo/components/CargoPaymentDocList.tsx
//
// Payment-document list with per-row download + (when allowed) delete (#1232).
// List/download is `cargo.read` (anyone who sees the row); delete is gated by
// the parent via `canManage`. Download exchanges the row id for a short-lived
// presigned URL (POST, empty body) and opens it in a new tab — never a public
// object URL.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CargoPaymentDocumentWire } from '@magimp/shared';

import { Button, ConfirmModal, EmptyState, Icon } from '../../../ui/index.js';
import { formatDateTime, formatSize } from '../../../i18n/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { cargoPaymentDocumentDownloadUrl } from '../../../api/cargo-payment-docs.js';
import { useDeleteCargoPaymentDocument } from '../mutations.js';

export interface CargoPaymentDocListProps {
  cargoId: string;
  documents: readonly CargoPaymentDocumentWire[];
  canManage: boolean;
}

export function CargoPaymentDocList({ cargoId, documents, canManage }: CargoPaymentDocListProps) {
  const { t } = useTranslation(['cargo']);
  if (documents.length === 0) {
    return <EmptyState icon="paperclip" title={t('cargo:payment.documents.empty')} />;
  }
  return (
    <ul
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
      data-testid="cargo-payment-doc-list"
    >
      {documents.map((doc) => (
        <DocRow key={doc.id} cargoId={cargoId} doc={doc} canManage={canManage} />
      ))}
    </ul>
  );
}

function DocRow({
  cargoId,
  doc,
  canManage,
}: {
  cargoId: string;
  doc: CargoPaymentDocumentWire;
  canManage: boolean;
}) {
  const { t } = useTranslation(['cargo', 'common']);
  const showToast = useToastStore((s) => s.show);
  const del = useDeleteCargoPaymentDocument(cargoId);
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Decision 7 (#1938): a document is removable only while still `pending`.
  // Pre-#1936 payloads omit `reviewStatus` entirely — treat absent as pending
  // (never yet reviewed), so manager attach/remove keeps working unchanged.
  const reviewStatus = doc.reviewStatus ?? 'pending';
  const canDelete = canManage && reviewStatus === 'pending';

  async function onDownload() {
    setDownloading(true);
    try {
      const { downloadUrl } = await cargoPaymentDocumentDownloadUrl(cargoId, doc.id);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showToast({ tone: 'error', text: errorMessage(err) });
    } finally {
      setDownloading(false);
    }
  }

  function onConfirmDelete() {
    del.mutate(doc.id, {
      onSuccess: () =>
        showToast({ tone: 'success', text: t('cargo:payment.documents.toasts.deleted') }),
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  }

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
      data-testid="cargo-payment-doc-row"
      data-doc-id={doc.id}
    >
      <Icon name="file-text" size={14} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 13,
            color: 'var(--fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={doc.fileName}
        >
          {doc.fileName}
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {formatSize(doc.contentLength)}
          {doc.amount ? ` · ${doc.amount}` : ''} ·{' '}
          {t(`cargo:payment.documents.reviewStatus.${reviewStatus}`)} ·{' '}
          {doc.uploadedBy?.name ?? t('cargo:payment.documents.uploaderDeleted')} ·{' '}
          {formatDateTime(doc.createdAt)}
        </span>
      </div>
      <Button
        size="sm"
        kind="ghost"
        icon="download"
        aria-label={t('cargo:payment.documents.download')}
        disabled={downloading}
        onClick={() => void onDownload()}
        data-testid="cargo-payment-doc-download"
      />
      {canDelete && (
        <Button
          size="sm"
          kind="ghost"
          icon="trash-2"
          aria-label={t('common:actions.delete')}
          onClick={() => setConfirming(true)}
          data-testid="cargo-payment-doc-delete"
        />
      )}
      {confirming && (
        <ConfirmModal
          title={t('cargo:payment.documents.confirmDelete.title')}
          text={t('cargo:payment.documents.confirmDelete.text', { name: doc.fileName })}
          danger
          confirmLabel={t('common:actions.delete')}
          onConfirm={onConfirmDelete}
          onClose={() => setConfirming(false)}
        />
      )}
    </li>
  );
}
